import React, { FC, useMemo } from "react";

import {
	Button,
	EditableCard,
	Grid,
	SectionCard,
	TField,
	TSelectOption,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";
import { useTour } from "@/hooks/useTour";

import { EditableAvatar } from "@/components/molecules";
import { RhythmCard } from "@/components/organisms/RhythmCard/RhythmCard";

import { meKeys } from "@/constants/CQueryKeys";

import { TUpdateMePayload, TUser, TUserSettings } from "@/types";

import { promiseToast } from "@/modules/index";

/** What `PATCH /me` takes — the card shows `mail` too, but it is not editable. */
const EDITABLE_FIELDS = ["firstName", "lastName", "phone", "timezone"] as const;
const CARD_FIELDS = [
	"firstName",
	"lastName",
	"mail",
	"phone",
	"timezone",
] as const;

/**
 * IANA zone names, label = value ("Europe/Paris"). `supportedValuesOf` is
 * missing in older engines (and jsdom): fall back to UTC alone rather
 * than an empty select.
 */
export const timezoneOptions = (): TSelectOption[] => {
	const zones =
		typeof Intl.supportedValuesOf === "function"
			? Intl.supportedValuesOf("timeZone")
			: ["UTC"];
	return zones.map((zone) => ({ value: zone, label: zone }));
};

/** The "Profil" tab: who I am (avatar, information) and how I work (my rhythm). */
export const ProfileTab: FC<{ me: TUser }> = ({ me }) => {
	// Built inside the component, not module-scope: `t()` here is i18next's
	// plain export (not the `useTranslation` hook), so a module-level
	// constant would call it at import time — before i18next.init()
	// resolves, resolving every label to its raw key/name instead of the
	// translated string.
	const fields: TField[] = useMemo(
		() => [
			{
				name: "firstName",
				type: "input",
				label: t("user.firstName"),
				required: true,
				autoComplete: "given-name",
			},
			{
				name: "lastName",
				type: "input",
				label: t("user.lastName"),
				required: true,
				autoComplete: "family-name",
			},
			// Shown for reference, not editable (the API ignores it on
			// PATCH): `disabled` on the descriptor reaches the input, and
			// `handleSubmit` keeps it out of the payload anyway.
			{
				name: "mail",
				type: "input",
				label: t("user.email"),
				disabled: true,
			},
			{
				name: "phone",
				type: "input",
				label: t("user.phone"),
				autoComplete: "tel",
			},
			{
				name: "timezone",
				type: "select",
				label: t("user.timezone"),
				options: timezoneOptions(),
				searchable: true,
				// The select hands back the option's value (a string, or
				// null when cleared); no clearing, a zone is always set.
				isClearable: false,
			},
		],
		[]
	);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				firstName: Yup.string().trim().required(t("forms.required")),
				lastName: Yup.string().trim().required(t("forms.required")),
				phone: Yup.string().max(32, t("forms.max", { value: 32 })),
			}),
		[]
	);

	const { user, updateUser } = useAuth();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { replay } = useTour("intro");

	// Forgotten, the tour opens by itself on the home page.
	const handleReplayTour = () => {
		replay();
		navigate("/");
	};

	// My rhythm: mine, not a view's, since the model rework (phase 4).
	const settingsQuery = useQuery({
		queryKey: meKeys.settings,
		queryFn: () => UserService.fetchSettings(),
	});

	const handleRhythmSave = (next: TUserSettings) => {
		const prom = UserService.updateSettings({
			workDays: next.workDays,
			hoursPerDay: next.hoursPerDay ?? "",
		}).then((saved) => {
			queryClient.setQueryData(meKeys.settings, saved);
			// Days and expected hours are computed from it.
			return queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "analytics" ||
					query.queryKey[0] === "views" ||
					query.queryKey[0] === "reports",
			});
		});
		promiseToast(prom);
		return prom;
	};

	const handleAvatarChange = async (file: File) => {
		const prom = UserService.updateAvatar(file).then((res) => {
			if (user) updateUser({ ...user, avatar: res.url });
			queryClient.setQueryData(meKeys.all, (oldData: TUser) => {
				return {
					...oldData,
					avatar: res.url,
				};
			});
			return res;
		});
		promiseToast(prom);
		return prom;
	};

	// Only what the API edits goes out: the card hands back every value it
	// was given (`mail` included), and a cleared phone stays `""` — the API
	// turns it into null.
	const handleSubmit = (values: object) => {
		const record = values as Record<string, unknown>;
		const payload = Object.fromEntries(
			EDITABLE_FIELDS.filter((name) => name in record).map((name) => [
				name,
				record[name],
			])
		) as TUpdateMePayload;
		const prom = UserService.updateMe(payload).then((updated) => {
			if (user) updateUser({ ...user, ...updated });
			queryClient.setQueryData(
				meKeys.all,
				(oldData: TUser | undefined) => ({
					...oldData,
					...updated,
				})
			);
			return updated;
		});
		promiseToast(prom);
		return prom.then(() => {});
	};

	// The card wants strings: a nullable field (phone) comes back as null.
	const formValues = Object.fromEntries(
		CARD_FIELDS.map((name) => [name, me[name] ?? ""])
	);

	return (
		<>
			<EditableAvatar
				className="mb-8 h-32 w-32"
				url={me.avatar}
				onChange={handleAvatarChange}
			/>
			<Grid columns={2} minItemWidth="400px">
				<EditableCard
					title={t("account.infos.title")}
					className="w-full"
					fields={fields}
					values={formValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
					editInModal
					formikWrapper
					editColumns={2}
					modalProps={{ size: "lg" }}
					// The app's own wording: the lib's defaults follow
					// AmphoreProvider's locale, not i18next's.
					editLabel={t("actions.edit")}
					submitLabel={t("actions.save")}
					cancelLabel={t("actions.cancel")}
				/>
				{!!settingsQuery.data && (
					<RhythmCard
						settings={settingsQuery.data}
						canEdit
						onSave={handleRhythmSave}
					/>
				)}
				<SectionCard
					title={t("tour.replay.title")}
					className="w-full"
					actions={
						<Button
							size="sm"
							variant="outline"
							picto="play"
							onClick={handleReplayTour}
						>
							{t("tour.replay.cta")}
						</Button>
					}
				>
					<p className="m-0 text-sm text-[var(--amp-color-sub)]">
						{t("tour.replay.description")}
					</p>
				</SectionCard>
			</Grid>
		</>
	);
};
